<template>
  <content-dialog
    v-model="dialogOpen"
    fullscreen-mobile
    no-backdrop-dismiss
    width="600px"
    title="Add Licence"
    :confirm-cancel="onCancelDialog"
    :tags="[helpCodes.createLicence, helpCodes.licences]"
  >
    <q-list dense>
      <detail-item
        v-if="!withUser"
        with-input
        icon="mdi-account"
        name="Staff Member"
        required
      >
        <user-input-popover
          v-model="licence.user"
          dense
          outlined
          icon="mdi-magnify"
          placeholder="Find Staff Member..."
          :error="!!errors['user_id']"
          :error-message="errors['user_id'] ? errors['user_id'][0] : null"
        />
      </detail-item>
      <detail-item
        with-input
        icon="mdi-earth"
        name="Location"
        required
      >
        <country-picker
          :state="licence.state_id"
          :error="!!errors['state_id']"
          :error-message="errors['state_id'] ? errors['state_id'][0] : null"
          mode="state"
          @set-state="(val) => licence.state_id = val"
        />
      </detail-item>
      <detail-item
        icon="mdi-card-account-details"
        name="Licence Number"
        with-input
        required
      >
        <q-input
          v-model="licence.licence_number"
          bottom-slots
          hide-bottom-space
          outlined
          dense
          counter
          :maxlength="50"
          :error="!!errors['licence_number']"
          :error-message="errors['licence_number'] ? errors['licence_number'][0] : null"
        />
      </detail-item>
      <detail-item
        icon="mdi-text-short"
        name="Title / Descriptor"
        with-input
      >
        <q-input
          v-model="licence.descriptor"
          bottom-slots
          hide-bottom-space
          :error="!!errors['descriptor']"
          :error-message="errors['descriptor'] ? errors['descriptor'][0] : null"
          outlined
          dense
          hint="Short title outlining the type of licence. Visible on client documents."
        />
      </detail-item>
      <detail-item
        icon="mdi-calendar"
        name="Expiry Date"
        with-input
      >
        <date-picker
          v-model="licence.expiry_date"
          :error="!!errors['expiry_date']"
          :error-message="errors['expiry_date'] ? errors['expiry_date'][0] : null"
          clearable
          dense
          outlined
          hint="We'll send you a notification if the licence is due to expire"
        />
      </detail-item>
      <detail-item
        name="Licence Document"
        icon="add_a_photo"
        :with-input="35"
      >
        <div v-if="licence.image_location === null">
          <q-btn
            label="Choose File"
            color="blue-7"
            icon="mdi-upload"
            @click="$refs.fileSelectDialog.open()"
          />
        </div>
        <div v-else>
          <file-view-card
            :file="licence.image_location"
            :error="errors['image_location'] ? errors['image_location'][0] : null"
            clickable
            @remove="licence.image_location = null"
            @details="viewingFileID = (licence.image_location ? licence.image_location.id : null)"
          />
        </div>
      </detail-item>
    </q-list>

    <template #bottom>
      <q-toolbar>
        <q-space />
        <q-btn
          label="Add Licence"
          icon="mdi-plus"
          color="green"
          @click="addNewLicence"
        />
      </q-toolbar>
    </template>
  </content-dialog>

  <file-select-dialog
    ref="fileSelectDialog"
    :preview-mode="false"
    @saved="(file) => licence.image_location = file"
  />
  <ViewFileDialog
    :id="viewingFileID"
    @close="viewingFileID = null"
    @saved="onFileSaved"
    @deleted="licence.image_location = null"
  />
</template>

<script>
import ViewFileDialog from '../../../files/partials/view/Dialog.vue'

function generateBlankLicence () {
  return {
    licence_number: null,
    descriptor: null,
    image_location: null,
    expiry_date: null,
    state_id: null,
    user: null
  }
}

export default {
  components: {
    ViewFileDialog
  },
  emits: ['created'],
  data () {
    return {
      licence: generateBlankLicence(),
      errors: {},
      dialogOpen: false,
      viewingFileID: null,
      withUser: null
    }
  },
  methods: {
    open (userId = null) {
      this.withUser = userId
      this.licence = generateBlankLicence()
      this.errors = {}
      this.dialogOpen = true
    },
    onCancelDialog (callback) {
      this.$q.dialog({
        title: 'Confirm',
        message: [
          'Are you sure you want to back?',
          'This licence will not be saved and any changes will be lost.'
        ],
        ok: true,
        cancel: true,
        color: 'orange-7'
      })
        .onOk(callback)
    },
    onFileSaved (file) {
      this.licence.image_location = file
    },
    async addNewLicence () {
      this.errors = {}

      this.$loading.show({ message: 'Adding licence...' })
      const { status, payload, errors } = await this.$post('v1/business/company/licences', {
        user_id: this.withUser || (this.licence.user ? this.licence.user.id : null),
        licence_number: this.licence.licence_number,
        descriptor: this.licence.descriptor,
        expiry_date: this.licence.expiry_date,
        state_id: this.licence.state_id,
        image_location: this.licence.image_location ? this.licence.image_location.id : null
      })
      this.$loading.hide()

      if (status === 'success') {
        this.$q.notify({
          message: 'Licence successfully created',
          color: 'green'
        })
        this.dialogOpen = false
        this.$emit('created', payload.licence)
      }
      else {
        this.errors = errors
        this.$nextTick(() => { this.scrollToFirstError(this.$el) })
      }
    }
  }
}
</script>
